import { useState } from "react";
import { Search, Plus, Pencil, Trash2, Users } from "lucide-react";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import {
  Select, Modal, Form, Input, InputNumber, DatePicker,
  Button, Tag, Popconfirm, Table, message, Tabs, Tooltip,
} from "antd";
import dayjs from "dayjs";
import { useStaffList, useStaffCreate, useStaffUpdate, useStaffDelete } from "../../../hooks/api/staff/hooks";
import { IStaffMember } from "../../../hooks/api/staff/callbacks";

const ROLE_OPTS = [
  { value: "manager", label: "Manager" },
  { value: "compliance_officer", label: "Compliance Officer" },
  { value: "office_admin", label: "Billing / Office Admin" },
];

const ROLE_COLOR: Record<string, string> = {
  manager: "purple",
  compliance_officer: "red",
  office_admin: "blue",
};

const EMP_OPTS = [
  { value: "employee", label: "Employee" },
  { value: "subcontractor", label: "Subcontractor" },
  { value: "contractor", label: "Contractor" },
  { value: "casual", label: "Casual" },
];

const TABS = [
  { key: "all", label: "All Staff" },
  { key: "manager", label: "Managers" },
  { key: "compliance_officer", label: "Compliance" },
  { key: "office_admin", label: "Billing / Admin" },
];

function fullName(s: IStaffMember) {
  return [s.first_name, s.last_name].filter(Boolean).join(" ") || "—";
}

function initials(s: IStaffMember) {
  return [s.first_name?.[0], s.last_name?.[0]].filter(Boolean).join("").toUpperCase() || "?";
}

function StaffModal({ open, editing, onClose, onSuccess }: {
  open: boolean; editing?: IStaffMember | null; onClose: () => void; onSuccess: () => void;
}) {
  const [form] = Form.useForm();
  const [messageApi, ctx] = message.useMessage();
  const isEdit = !!editing;

  const { mutate: create, isPending: creating } = useStaffCreate({
    onSuccess: () => { messageApi.success("Staff member created."); onSuccess(); },
    onError: (e: any) => messageApi.error(e?.response?.data?.message || e?.message || "Failed."),
  });
  const { mutate: update, isPending: updating } = useStaffUpdate({
    onSuccess: () => { messageApi.success("Staff member updated."); onSuccess(); },
    onError: (e: any) => messageApi.error(e?.response?.data?.message || e?.message || "Failed."),
  });

  const handleOpen = (v: boolean) => {
    if (!v) return;
    if (editing) {
      form.setFieldsValue({
        first_name: editing.first_name,
        last_name: editing.last_name,
        email: editing.email,
        phone: editing.phone,
        mobile: editing.mobile,
        job_title: editing.job_title,
        employee_number: editing.employee_number,
        role_slug: editing.role_slug,
        employment_type: editing.employment_type ?? "employee",
        hourly_rate: editing.hourly_rate,
        postcode: editing.postcode,
        start_date: editing.start_date ? dayjs(editing.start_date) : null,
        end_date: editing.end_date ? dayjs(editing.end_date) : null,
        status: editing.status,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ employment_type: "employee", status: "ACTIVE" });
    }
  };

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const payload = {
        ...values,
        start_date: values.start_date ? values.start_date.format("YYYY-MM-DD") : null,
        end_date: values.end_date ? values.end_date.format("YYYY-MM-DD") : null,
      };
      if (isEdit && editing) {
        update({ ...payload, id: editing.id });
      } else {
        create(payload);
      }
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      afterOpenChange={handleOpen}
      title={isEdit ? `Edit — ${fullName(editing!)}` : "Add Staff Member"}
      width={640}
      footer={null}
      destroyOnClose
    >
      {ctx}
      <Form form={form} layout="vertical" size="small" className="mt-2" autoComplete="off">
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="first_name" label="First Name" rules={[{ required: true }]}>
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item name="last_name" label="Last Name">
            <Input autoComplete="off" />
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="email" label="Email" rules={[{ type: "email" }]}>
            <Input autoComplete="new-email" />
          </Form.Item>
          <Form.Item name="phone" label="Phone">
            <Input autoComplete="off" />
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="mobile" label="Mobile">
            <Input autoComplete="off" />
          </Form.Item>
          {!isEdit && (
            <Form.Item name="password" label="Password" rules={[{ required: true, min: 6 }]}>
              <Input.Password placeholder="Min 6 characters" autoComplete="new-password" />
            </Form.Item>
          )}
        </div>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="role_slug" label="Role" rules={[{ required: true }]}>
            <Select options={ROLE_OPTS} placeholder="Select role" />
          </Form.Item>
          <Form.Item name="job_title" label="Job Title">
            <Input autoComplete="off" />
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="employee_number" label="Employee Number">
            <Input placeholder="EMP-001" autoComplete="off" />
          </Form.Item>
          <Form.Item name="employment_type" label="Employment Type">
            <Select options={EMP_OPTS} />
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="hourly_rate" label="Hourly Rate (£)">
            <InputNumber min={0} step={0.01} prefix="£" className="w-full" />
          </Form.Item>
          <Form.Item name="postcode" label="Postcode">
            <Input autoComplete="off" />
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="start_date" label="Start Date">
            <DatePicker className="w-full" format="DD/MM/YYYY" />
          </Form.Item>
          {isEdit && (
            <Form.Item name="end_date" label="End Date">
              <DatePicker className="w-full" format="DD/MM/YYYY" />
            </Form.Item>
          )}
        </div>
        {isEdit && (
          <Form.Item name="status" label="Status">
            <Select options={[{ value: "ACTIVE", label: "Active" }, { value: "INACTIVE", label: "Inactive" }]} />
          </Form.Item>
        )}
        <div className="flex justify-end gap-2 mt-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={creating || updating} onClick={handleSubmit}>
            {isEdit ? "Save" : "Add Staff Member"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}

const StaffPage = () => {
  const [roleFilter, setRoleFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<IStaffMember | null>(null);
  const [messageApi, ctx] = message.useMessage();

  const queryParams = {
    page,
    limit: 20,
    ...(roleFilter !== "all" ? { role_slug: roleFilter } : {}),
    ...(search ? { search } : {}),
  };

  const { data: listData, isLoading, refetchWithQuery } = useStaffList(queryParams);
  const staff: IStaffMember[] = listData?.data ?? [];
  const total = Number((listData as any)?.extra?.total ?? 0);

  const { mutate: deleteStaff } = useStaffDelete({
    onSuccess: () => { messageApi.success("Staff member deleted."); refetchWithQuery({ page }); },
    onError: (e: any) => messageApi.error(e?.response?.data?.message || e?.message || "Failed."),
  });

  const columns = [
    {
      title: "Name",
      key: "name",
      render: (_: any, s: IStaffMember) => (
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-purple-100 text-purple-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
            {initials(s)}
          </div>
          <div>
            <div className="font-medium text-slate-800 text-sm">{fullName(s)}</div>
            <div className="text-xs text-slate-400">{s.job_title || "—"}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Role",
      key: "role",
      render: (_: any, s: IStaffMember) => (
        <Tag color={ROLE_COLOR[s.role_slug] ?? "default"}>{s.role_name}</Tag>
      ),
    },
    {
      title: "Contact",
      key: "contact",
      render: (_: any, s: IStaffMember) => (
        <div className="text-xs text-slate-500">
          {s.email && <div>{s.email}</div>}
          {s.phone && <div>{s.phone}</div>}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v: string) => <Tag color={v === "ACTIVE" ? "green" : "default"}>{v}</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, s: IStaffMember) => (
        <div className="flex items-center gap-1">
          <Tooltip title="Edit">
            <Button size="small" icon={<Pencil size={11} />} onClick={() => { setEditing(s); setModalOpen(true); }} />
          </Tooltip>
          <Popconfirm title="Delete this staff member?" onConfirm={() => deleteStaff(s.id)} okText="Delete" okButtonProps={{ danger: true }}>
            <Tooltip title="Delete">
              <Button size="small" icon={<Trash2 size={11} />} danger type="text" />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6 min-h-screen">
      {ctx}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
            <Users size={20} className="text-purple-600" /> Staff Management
          </h1>
          <p className="text-slate-500 text-sm">Managers, compliance officers & billing staff</p>
        </div>
        <ButtonWrapper type="primary" icon={<Plus size={14} />} onClick={() => { setEditing(null); setModalOpen(true); }} className="flex items-center gap-2">
          Add Staff Member
        </ButtonWrapper>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Tabs
            activeKey={roleFilter}
            onChange={(k) => { setRoleFilter(k); setPage(1); refetchWithQuery({ page: 1, role_slug: k === "all" ? undefined : k }); }}
            items={TABS.map((t) => ({ key: t.key, label: t.label }))}
            className="flex-1"
            style={{ marginBottom: 0 }}
          />
          <div className="relative w-56">
            <Search size={14} className="text-slate-400 absolute left-3 top-2.5 z-10" />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); refetchWithQuery({ search: e.target.value, page: 1 }); setPage(1); }}
              placeholder="Search staff…"
              className="pl-9 pr-4 h-10 text-sm border border-slate-200 rounded-lg w-full bg-white focus:outline-none focus:border-primary"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <Table
          dataSource={staff}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{ current: page, pageSize: 20, total, showSizeChanger: false, showTotal: (t) => `${t} staff members`, onChange: (p) => { setPage(p); refetchWithQuery({ page: p }); } }}
          scroll={{ x: 700 }}
        />
      </div>

      <StaffModal
        open={modalOpen}
        editing={editing}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSuccess={() => { setModalOpen(false); setEditing(null); refetchWithQuery({ page }); }}
      />
    </div>
  );
};

export default StaffPage;
